// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import { enablePostalCodeLocationFinder } from "@bg/App/Store/Selectors/PostalCodeSelector";
import { constants } from "@bg/XDL/constants";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { hasLocalStorage } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const { fulfillmentMethod, isAuthenticated, rememberMe, isGuest, shipToId, pickUpWarehouse, properties } =
        getSession(state);
    const shipTo = getShipToState(state, shipToId).value;

    return {
        fulfillmentMethod,
        isAuthenticated,
        rememberMe,
        isGuest,
        shipTo,
        pickUpWarehouse,
        isDrawerOpen: state.components.addressDrawer.isOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        resetAddress: state.commonActions["resetShipToAddress"],
        properties,
        enablePostalCodeLocationFinder: enablePostalCodeLocationFinder(state),
    };
};

const mapDispatchToProps = {
    setDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface HeaderShipToAddressStyles {
    shipToAddressWrapper?: InjectableCss;
    titleTypography?: TypographyPresentationProps;
}

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    shipToAddressWrapper: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: flex-start;
            align-items: center;

            .name-phone-wrapper {
                font-size: ${fontConfig.fontSize.xSmall}px;
            }
        `,
    },
    titleTypography: {
        css: css`
            margin-right: 5px;
            font-weight: ${fontConfig.weight.semiBold};
            font-size: ${fontConfig.fontSize.xSmall}px;
            line-height: ${fontConfig.lineHeight.singleLineSmall};
        `,
        ellipsis: true,
    },
};

interface OwnProps extends WidgetProps {
    fields: {};
}

const styles = headerShipToAddressStyles;

const HeaderShipToAddress: FC<Props> = ({ isAuthenticated, properties, enablePostalCodeLocationFinder }) => {
    let addressLabel = "";
    let phoneNumber = "";

    // BG-2223: Support Desk - Header Warehouse Display.
    const pickupWarehouseInfo = properties?.currentWarehouseInfo ? JSON.parse(properties?.currentWarehouseInfo) : {};
    if (pickupWarehouseInfo) {
        addressLabel = `${pickupWarehouseInfo.description || pickupWarehouseInfo.name}`;
        phoneNumber = pickupWarehouseInfo.phone;
    }

    // BG-2247: Header is assigning "Airdrie" Warehouse to Guest Users automatically
    if (enablePostalCodeLocationFinder && !hasLocalStorage(constants.localStorage.POSTAL_CODE_ID) && !isAuthenticated) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.shipToAddressWrapper}>
                <Typography
                    size={fontConfig.fontSize.xSmall}
                    lineHeight={fontConfig.lineHeight.singleLineSmall}
                    weight={fontConfig.weight.semiBold}
                    color="common.white"
                    data-test-selector="header_shipToLabel"
                    className="branch-name-lbl"
                >
                    {translate("Branch:")}&nbsp;
                </Typography>
                <StyledWrapper className="name-phone-wrapper">
                    <Typography
                        color="common.white"
                        {...styles.titleTypography}
                        data-test-selector="header_shipToLabel"
                    >
                        {addressLabel}
                    </Typography>
                    {isAuthenticated && phoneNumber && (
                        <a
                            href={`tel:${phoneNumber}`}
                            style={{
                                fontSize: fontConfig.fontSize.xSmall,
                                lineHeight: fontConfig.lineHeight.singleLineSmall,
                                color: "white",
                                fontWeight: fontConfig.weight.semiBold,
                            }}
                            data-test-selector="header_shipToLabel"
                        >
                            <span>&nbsp;&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;&nbsp;</span>
                            {phoneNumber}
                        </a>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderShipToAddress)),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "XNGAGE" as any,
    },
};

export default widgetModule;
